import { ThemeOptions } from "@mui/material";

export type AppDarkThemeName = "dark" | "dark-contrast" | "tech" | "navy";

export interface AppDarkThemePalette {
  background: string;
  card: string;
  elevated: string;
  hover: string;
  selected: string;
  input: string;
  border: string;
  borderSecondary: string;
  text: string;
  textSecondary: string;
  muted: string;
  accent: string;
  accentHover: string;
  accentForeground: string;
}

export const DARK_THEME_PALETTES: Record<
  AppDarkThemeName,
  AppDarkThemePalette
> = {
  dark: {
    background: "#0A0A0C",
    card: "#101014",
    elevated: "#18181D",
    hover: "#15151A",
    selected: "#1A1A20",
    input: "#121217",
    border: "#1F1F26",
    borderSecondary: "#33333F",
    text: "#EDEDF0",
    textSecondary: "#8A8A95",
    muted: "#5C5C67",
    accent: "#6B57FF",
    accentHover: "#5F4BF2",
    accentForeground: "#8B8BF5",
  },
  "dark-contrast": {
    background: "#202020",
    card: "#191919",
    elevated: "#303030",
    hover: "#202020",
    selected: "#2C2C2C",
    input: "#2C2C2C",
    border: "#7A7A7A",
    borderSecondary: "#8A8A8A",
    text: "#FAFAFA",
    textSecondary: "#D4D4D4",
    muted: "#898989",
    accent: "#6B57FF",
    accentHover: "#5F4BF2",
    accentForeground: "#AAA3FF",
  },
  tech: {
    background: "#0A2540",
    card: "#142C4A",
    elevated: "#1C3B66",
    hover: "#1C3B66",
    selected: "#244A80",
    input: "#0A2540",
    border: "#4263EB",
    borderSecondary: "#6685F0",
    text: "#D6E4FF",
    textSecondary: "#A3BFFA",
    muted: "#6E87AD",
    accent: "#2563EB",
    accentHover: "#1D4ED8",
    accentForeground: "#93C5FD",
  },
  navy: {
    background: "#0B111D",
    card: "#141D2C",
    elevated: "#202D40",
    hover: "#344054",
    selected: "#1E3D5B",
    input: "#344054",
    border: "#344054",
    borderSecondary: "#475467",
    text: "#D0D5DD",
    textSecondary: "#98A2B3",
    muted: "#667085",
    accent: "#2563EB",
    accentHover: "#1D4ED8",
    accentForeground: "#93C5FD",
  },
};

export const getDarkTheme = (
  themeName: AppDarkThemeName = "dark"
): ThemeOptions => {
  const colors = DARK_THEME_PALETTES[themeName];

  return {
    palette: {
      mode: "dark",
      primary: {
        main: colors.accent,
        light: colors.accentForeground,
        dark: colors.accentHover,
      },
      background: {
        default: colors.background,
        paper: colors.card,
      },
      text: {
        primary: colors.text,
        secondary: colors.textSecondary,
      },
      divider: colors.border,
      action: {
        active: colors.text,
        hover: colors.hover,
        selected: colors.selected,
        disabled: colors.muted,
        disabledBackground: colors.elevated,
      },
    },
    shape: {
      borderRadius: 9,
    },
    typography: {
      fontFamily:
        "Manrope, Inter, Roboto, -apple-system, BlinkMacSystemFont, sans-serif",
      button: {
        textTransform: "none",
        fontWeight: 700,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: colors.background,
            color: colors.text,
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            backgroundColor: colors.input,
            borderRadius: 9,
            color: colors.text,
          },
          input: {
            "&:-webkit-autofill": {
              "-webkit-box-shadow": `0 0 0 100px ${colors.input} inset !important`,
              "-webkit-text-fill-color": colors.text,
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            minHeight: "32px",
            borderRadius: "8px",
            padding: "6px 12px",
          },
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: {
            height: "auto",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            borderColor: colors.border,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            borderColor: colors.border,
          },
          root: {
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.borderSecondary,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.accent,
            },
          },
        },
      },
    },
  };
};

export const darkTheme = getDarkTheme();
